import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Package } from 'lucide-react'
import api from '../../lib/api'

interface LowStockProduct {
  id: string
  name: string
  currentStock: number
  minStock: number
  unit: string
}

const COOLDOWN_MS = 30 * 60 * 1000
const TOAST_ID = 'low-stock-global'
const STORAGE_KEY = 'low-stock-last-shown'

export function LowStockGlobalAlert() {
  const location = useLocation()
  const navigate = useNavigate()
  const lastShownRef = useRef<number>(0)

  const { data: lowStock } = useQuery<LowStockProduct[]>({
    queryKey: ['inventory-low-stock'],
    queryFn: async () => {
      const { data } = await api.get('/inventory/low-stock')
      return data
    },
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
  })

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) lastShownRef.current = Number(stored) || 0
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    if (!lowStock || lowStock.length === 0) return
    if (location.pathname.startsWith('/inventory')) {
      toast.dismiss(TOAST_ID)
      return
    }
    const now = Date.now()
    if (now - lastShownRef.current < COOLDOWN_MS) return

    lastShownRef.current = now
    try {
      localStorage.setItem(STORAGE_KEY, String(now))
    } catch {
      // ignore
    }

    toast.custom(
      (t) => (
        <div
          className={`max-w-md w-full bg-surface-1 border border-warning-500 rounded-xl shadow-xl p-4 flex items-start gap-3 ${
            t.visible ? 'animate-enter' : 'animate-leave'
          }`}
        >
          <div className="p-2 rounded-lg bg-warning-50 text-warning-600 flex-shrink-0">
            <Package className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-text-primary">
              {lowStock.length} productos en stock bajo
            </p>
            <p className="text-xs text-text-secondary mt-1 truncate">
              {lowStock.slice(0, 3).map((p) => p.name).join(', ')}
              {lowStock.length > 3 ? ` y ${lowStock.length - 3} mas` : ''}
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => {
                  toast.dismiss(t.id)
                  navigate('/inventory')
                }}
                className="text-xs font-medium bg-warning-500 text-white px-3 py-1.5 rounded-lg hover:bg-warning-700"
              >
                Ver inventario
              </button>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="text-xs font-medium text-text-secondary px-3 py-1.5 rounded-lg hover:bg-surface-2"
              >
                Recordar despues
              </button>
            </div>
          </div>
        </div>
      ),
      {
        id: TOAST_ID,
        duration: Infinity,
        position: 'top-right',
      },
    )
  }, [lowStock, location.pathname, navigate])

  return null
}
